import * as THREE from 'three';
import { physicsWorld, getRapier } from './physics.js';
import { config } from './config.js';
import { rigidBodies } from './objects-shared.js';

let htmlPages = [];

function loadAndDrawImage(ctx, imagePath, x, y, width, height) {
    return new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        
        const timeout = setTimeout(() => {
            console.warn(`Image load timeout: ${imagePath}`);
            ctx.fillStyle = '#3a3a3a';
            ctx.fillRect(x, y, width, height);
            resolve();
        }, 5000);
        
        img.onload = () => {
            clearTimeout(timeout);
            const imgAspect = img.width / img.height;
            const targetAspect = width / height;
            
            let drawWidth = width;
            let drawHeight = height;
            let offsetX = 0;
            let offsetY = 0;
            
            if (imgAspect > targetAspect) {
                drawHeight = width / imgAspect;
                offsetY = (height - drawHeight) / 2;
            } else {
                drawWidth = height * imgAspect;
                offsetX = (width - drawWidth) / 2;
            }
            
            ctx.fillStyle = 'rgba(0, 0, 0, 0)';
            ctx.fillRect(x, y, width, height);
            ctx.drawImage(img, x + offsetX, y + offsetY, drawWidth, drawHeight);
            ctx.strokeStyle = '#00000000';
            ctx.lineWidth = 0;
            ctx.strokeRect(x, y, width, height);
            
            resolve();
        };
        
        img.onerror = (error) => {
            clearTimeout(timeout);
            console.warn(`Failed to load image: ${imagePath}`, error);
            ctx.fillStyle = '#2a2a2a';
            ctx.fillRect(x, y, width, height);
            resolve();
        };
        
        img.src = imagePath;
    });
}

function createVideoElement(videoPath, x, y, width, height) {
    const video = document.createElement('video');
    video.src = videoPath;
    video.crossOrigin = 'anonymous';
    video.loop = true;
    video.muted = true;
    video.autoplay = true;
    video.playsInline = true;
    video.style.display = 'none';
    document.body.appendChild(video);
    
    video.play().catch(e => console.warn('Video autoplay failed:', e));
    
    return {
        video: video,
        x: x,
        y: y,
        width: width,
        height: height
    };
}

function wrapText(ctx, text, x, y, maxWidth, lineHeight) {
    const words = text.split(' ');
    let line = '';
    let lineCount = 0;
    
    for (let n = 0; n < words.length; n++) {
        const testLine = line + words[n] + ' ';
        const metrics = ctx.measureText(testLine);
        const testWidth = metrics.width;
        
        if (testWidth > maxWidth && n > 0) {
            ctx.fillText(line, x, y + (lineCount * lineHeight));
            line = words[n] + ' ';
            lineCount++;
        } else {
            line = testLine;
        }
    }
    ctx.fillText(line, x, y + (lineCount * lineHeight));
}

async function createHTMLCanvas() {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    canvas.width = 2048;
    canvas.height = 2896;
    
    ctx.fillStyle = 'rgba(0, 0, 0, 0)';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    const margin = 120;
    const contentWidth = canvas.width - (margin * 2);
    let yPos = margin;
    
    const videoElements = [];
    
    ctx.fillStyle = '#ffffffff';
    ctx.font = 'bold 120px Arial, sans-serif';
    ctx.fillText('Welcome to 3D Web Experience', margin, yPos);
    yPos += 100;
    
    ctx.strokeStyle = '#3b82f6';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(margin, yPos);
    ctx.lineTo(canvas.width - margin, yPos);
    ctx.stroke();
    yPos += 80;
    
    ctx.fillStyle = '#c8c8c8ff';
    ctx.font = '64px Arial, sans-serif';
    const text1 = 'This is an interactive 3D web page rendered in Three.js. You can shoot at it, move it with physics, and interact with it just like any other object in the scene.';
    wrapText(ctx, text1, margin, yPos, contentWidth, 55);
    yPos += 250;
    
    await loadAndDrawImage(ctx, './img/sny.svg', margin, yPos, contentWidth, 800);
    yPos += 480;
    
    ctx.fillStyle = '#e100ffff';
    ctx.font = 'bold 56px Arial, sans-serif';
    ctx.fillText('Interactive Features', margin, yPos);
    yPos += 120;
    
    ctx.fillStyle = '#fffffff9';
    ctx.font = '36px, sans-serif';
    const features = [
        '• Physics simulation with Rapier3D',
        '• Real-time collision detection',
        '• Dynamic texture rendering',
        '• Interactive HTML-to-3D conversion',
        '• Responsive design in 3D space'
    ];
    
    features.forEach(feature => {
        ctx.fillText(feature, margin + 20, yPos);
        yPos += 60;
    });
    yPos += 60;
    
    ctx.font = '38px Arial, sans-serif';
    const text2 = 'The page you are looking at is completely dynamic. It can be updated in real-time, allowing for live content changes without reloading the 3D scene.';
    wrapText(ctx, text2, margin, yPos, contentWidth, 55);
    yPos += 120;
    
    ctx.fillStyle = '#2845ffff';
    ctx.font = '36px Arial, sans-serif';
    ctx.fillText('Visit: threejs.org', margin + 40, yPos);
    yPos += 30;

    await loadAndDrawImage(ctx, './img/orka.svg', margin, yPos, contentWidth, 1000);
    yPos += 600;
    
    ctx.fillStyle = '#6b7280';
    ctx.font = '32px Arial, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('© 2025 3D Web Experience - Powered by Three.js', canvas.width / 2, yPos);
    
    return { canvas, videoElements };
}

async function createFullscreenVideoCanvas(videoPath) {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    canvas.width = 2520;
    canvas.height = 1432;
    
    const videoElements = [];
    
    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    const videoData = createVideoElement(videoPath, 0, 0, canvas.width, canvas.height);
    videoElements.push(videoData);
    
    return { canvas, videoElements };
}

export async function createHTMLPage(scene, position = { x: 0, y: 0, z: 0 }, rotation = { x: 0, y: 0, z: 0 }, scale = 3) {
    const RAPIER = getRapier();
    
    const { canvas: htmlCanvas, videoElements } = await createHTMLCanvas();
    
    const texture = new THREE.CanvasTexture(htmlCanvas);
    texture.minFilter = THREE.LinearFilter;
    texture.magFilter = THREE.LinearFilter;
    texture.anisotropy = 16;
    
    const pageWidth = 30 * scale;
    const pageHeight = (htmlCanvas.height / htmlCanvas.width) * pageWidth;
    
    const geometry = new THREE.PlaneGeometry(pageWidth, pageHeight);
    
    const material = new THREE.MeshStandardMaterial({
        map: texture,
        side: THREE.DoubleSide,
        metalness: 0.0,
        roughness: 0.2,
        emissive: 0x000000,
        emissiveIntensity: 0.0,
        transparent: true
    });
    
    const pageMesh = new THREE.Mesh(geometry, material);
    pageMesh.position.set(position.x, position.y, position.z);
    pageMesh.rotation.set(rotation.x, rotation.y, rotation.z);
    pageMesh.castShadow = true;
    pageMesh.receiveShadow = true;
    
    scene.add(pageMesh);
    
    const quaternion = new THREE.Quaternion();
    quaternion.setFromEuler(pageMesh.rotation);
    
    const bodyDesc = RAPIER.RigidBodyDesc.dynamic()
        .setTranslation(position.x, position.y, position.z)
        .setRotation({ x: quaternion.x, y: quaternion.y, z: quaternion.z, w: quaternion.w })
        .setLinearDamping(0.5)
        .setAngularDamping(0.8)
        .setCanSleep(false);
    
    const body = physicsWorld.createRigidBody(bodyDesc);
    
    const thickness = 10;
    const colliderDesc = RAPIER.ColliderDesc.cuboid(
        pageWidth / 2,
        pageHeight / 2,
        thickness / 2
    )
        .setRestitution(0.3)
        .setFriction(0.6)
        .setDensity(0.5);
    
    physicsWorld.createCollider(colliderDesc, body);
    
    pageMesh.userData.physicsBody = body;
    pageMesh.userData.isHTMLPage = true;
    pageMesh.userData.texture = texture;
    pageMesh.userData.canvas = htmlCanvas;
    pageMesh.userData.videoElements = videoElements;
    
    pageMesh.userData.updateVideos = function() {
        if (!videoElements || videoElements.length === 0) return;
        
        const ctx = htmlCanvas.getContext('2d');
        
        videoElements.forEach(videoData => {
            const video = videoData.video;
            
            if (video.readyState >= video.HAVE_CURRENT_DATA) {
                const videoAspect = video.videoWidth / video.videoHeight;
                const targetAspect = videoData.width / videoData.height;
                
                let drawWidth = videoData.width;
                let drawHeight = videoData.height;
                let offsetX = 0;
                let offsetY = 0;
                
                if (videoAspect > targetAspect) {
                    drawHeight = videoData.width / videoAspect;
                    offsetY = (videoData.height - drawHeight) / 2;
                } else {
                    drawWidth = videoData.height * videoAspect;
                    offsetX = (videoData.width - drawWidth) / 2;
                }
                
                ctx.fillStyle = '#000000';
                ctx.fillRect(videoData.x, videoData.y, videoData.width, videoData.height);
                
                ctx.drawImage(
                    video, 
                    videoData.x + offsetX, 
                    videoData.y + offsetY, 
                    drawWidth, 
                    drawHeight
                );
                
                ctx.strokeStyle = '#3b82f6';
                ctx.lineWidth = 3;
                ctx.strokeRect(videoData.x, videoData.y, videoData.width, videoData.height);
            }
        });
        
        texture.needsUpdate = true;
    };
    
    rigidBodies.push(pageMesh);
    htmlPages.push(pageMesh);
    
    return pageMesh;
}

export async function createVideoPage(scene, videoPath, position = { x: 60, y: 50, z: -100 }, rotation = { x: 0, y: 0, z: 0 }, scale = 2) {
    const RAPIER = getRapier();
    
    const { canvas: htmlCanvas, videoElements } = await createFullscreenVideoCanvas(videoPath);
    
    const texture = new THREE.CanvasTexture(htmlCanvas);
    texture.minFilter = THREE.LinearFilter;
    texture.magFilter = THREE.LinearFilter;
    texture.anisotropy = 16;
    
    const pageWidth = 40 * scale;
    const pageHeight = (htmlCanvas.height / htmlCanvas.width) * pageWidth;
    
    const geometry = new THREE.PlaneGeometry(pageWidth, pageHeight);
    
    const material = new THREE.MeshStandardMaterial({
        map: texture,
        side: THREE.DoubleSide,
        metalness: 0.8,
        roughness: 0.1,
        emissive: 0x000000,
        emissiveIntensity: 0
    });
    
    const pageMesh = new THREE.Mesh(geometry, material);
    pageMesh.position.set(position.x, position.y, position.z);
    pageMesh.rotation.set(rotation.x, rotation.y, rotation.z);
    pageMesh.castShadow = true;
    pageMesh.receiveShadow = true;
    
    scene.add(pageMesh);
    
    const quaternion = new THREE.Quaternion();
    quaternion.setFromEuler(pageMesh.rotation);
    
    const bodyDesc = RAPIER.RigidBodyDesc.dynamic()
        .setTranslation(position.x, position.y, position.z)
        .setRotation({ x: quaternion.x, y: quaternion.y, z: quaternion.z, w: quaternion.w })
        .setLinearDamping(0.5)
        .setAngularDamping(0.8)
        .setCanSleep(false);
    
    const body = physicsWorld.createRigidBody(bodyDesc);
    
    const thickness = 5;
    const colliderDesc = RAPIER.ColliderDesc.cuboid(
        pageWidth / 2,
        pageHeight / 2,
        thickness / 2
    )
        .setRestitution(0.3)
        .setFriction(0.6)
        .setDensity(0.5);
    
    physicsWorld.createCollider(colliderDesc, body);
    
    pageMesh.userData.physicsBody = body;
    pageMesh.userData.isHTMLPage = true;
    pageMesh.userData.texture = texture;
    pageMesh.userData.canvas = htmlCanvas;
    pageMesh.userData.videoElements = videoElements;
    
    pageMesh.userData.updateVideos = function() {
        if (!videoElements || videoElements.length === 0) return;
        
        const ctx = htmlCanvas.getContext('2d');
        
        videoElements.forEach(videoData => {
            const video = videoData.video;
            
            if (video.readyState >= video.HAVE_CURRENT_DATA) {
                const videoAspect = video.videoWidth / video.videoHeight;
                const targetAspect = videoData.width / videoData.height;
                
                let drawWidth = videoData.width;
                let drawHeight = videoData.height;
                let offsetX = 0;
                let offsetY = 0;
                
                if (videoAspect > targetAspect) {
                    drawHeight = videoData.width / videoAspect;
                    offsetY = (videoData.height - drawHeight) / 2;
                } else {
                    drawWidth = videoData.height * videoAspect;
                    offsetX = (videoData.width - drawWidth) / 2;
                }
                
                ctx.fillStyle = '#000000';
                ctx.fillRect(videoData.x, videoData.y, videoData.width, videoData.height);
                
                ctx.drawImage(
                    video, 
                    videoData.x + offsetX, 
                    videoData.y + offsetY, 
                    drawWidth, 
                    drawHeight
                );
            }
        });
        
        texture.needsUpdate = true;
    };
    
    rigidBodies.push(pageMesh);
    htmlPages.push(pageMesh);
    
    return pageMesh;
}

export async function createAllPages(scene) {
    await createHTMLPage(scene, { x: 0, y: 0, z: -23 }, { x: 0, y: 0, z: 0 }, 1);
    await createVideoPage(scene, './img/new.mp4', { x: 60, y: 50, z: -100 }, { x: 0, y: Math.PI / 8, z: 0 }, 2);
}

export function updateHTMLPageVideos() {
    htmlPages.forEach(page => {
        if (page.userData.updateVideos) {
            page.userData.updateVideos();
        }
    });
}

export function getHTMLPages() {
    return htmlPages;
}

export function disposePages() {
    htmlPages.forEach(page => {
        if (page.geometry) page.geometry.dispose();
        if (page.material) page.material.dispose();
        if (page.userData.texture) page.userData.texture.dispose();
        if (page.userData.physicsBody) {
            physicsWorld.removeRigidBody(page.userData.physicsBody);
        }
        if (page.userData.videoElements) {
            page.userData.videoElements.forEach(vData => {
                if (vData.video) {
                    vData.video.pause();
                    vData.video.remove();
                }
            });
        }
    });
    htmlPages = [];
}